import React, { useState } from 'react';
import { MobileModal } from '@wajeez-web/components/ui';
import { Content, Server } from '@wajeez-web/core/api-base';
import {
  BaseComponentProps,
  useComponentData,
} from '@wajeez-web/components/page-view-builder';

import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import {
  CircleListItem,
  HorizontalListItem,
  LargeRectangleItem,
} from './cards';
import { useContentSelector } from '@wajeez-web/stores/content';
import { ImSpinner8 } from 'react-icons/im';

export interface VerticalListParameters {
  title: string | null;
  seeAllTitle: string | null;
  withProgress: 'False' | 'True';
}

interface SeeMoreModalProps {
  title: string | null | undefined;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  component?: Server.ComponentWithUniqueIdOutput | null;
  context?: Content.ContentEntityOutput | null;
  type?: string | undefined;
  isBottomBorder?: boolean;
}

export const SeeMoreModal: React.FC<SeeMoreModalProps> = ({
  title,
  isOpen,
  setIsOpen,
  component,
  context,
  type = 'COM:HORIZONTAL:LIST',
  isBottomBorder = true,
}) => {
  const [pageNumber, setPageNumber] = useState(2);

  const contentEntityBySelectorStatus = useContentSelector(
    ({ contentEntity }) => contentEntity.contentEentityBySelectorStatus
  );

  const { contents, meta } = useComponentData<VerticalListParameters>(
    component,
    context,
    pageNumber
  );

  const { lastElementRef } = useInfiniteScroll<HTMLDivElement>(
    meta.hasNextPage,
    setPageNumber
  );

  const listItem = (
    ItemComponent: React.ForwardRefExoticComponent<
      BaseComponentProps & React.RefAttributes<HTMLDivElement>
    >,
    content: Content.ContentEntityOutput,
    i: number
  ) => {
    return (
      <ItemComponent
        ref={contents.length - 1 === i ? lastElementRef : null}
        key={content.id}
        component={{ ...component?.itemTemplate, uniqueId: i }}
        context={content}
        isLastSibling={contents.length - 1 === i}
        type={type}
      />
    );
  };

  const renderItem = (content: Content.ContentEntityOutput, i: number) => {
    switch (type) {
      case 'COM:HORIZONTAL:LIST':
        return listItem(HorizontalListItem, content, i);
      case 'COM:CIRCLE:LIST':
        return listItem(CircleListItem, content, i);
      case 'COM:LARGE:RECTANGLE:LIST':
        return listItem(LargeRectangleItem, content, i);
      default:
        return null;
    }
  };

  return (
    <MobileModal
      open={isOpen}
      title={title}
      onClose={() => setIsOpen(false)}
      width="full"
      height="screen"
      headerStyle="px-4 pt-5 pb-0 sm:p-6 sm:pb-4 mb-4 font-bold text-center"
      zIndex={50}
    >
      <div className="w-full h-screen overflow-y-auto px-4 pb-[170px]">
        <div
          className="absolute top-[-35px] right-[16px] w-[16px] h-[18px]"
          onClick={() => setIsOpen(false)}
        >
          <img
            src="/assets/icons/arrow-back-mobile.svg"
            alt="back-icon"
            className="w-full h-full"
          />
        </div>
        <div className={`w-full`}>
          {contents.map((content, i) => renderItem(content, i))}
        </div>
        {contentEntityBySelectorStatus[component?.id + (context?.id ?? '')] ===
          'loading' && (
          <div className="w-full pt-2 text-[24px] text-primary dark:text-primary-dark">
            <ImSpinner8 className="animate-spin mx-auto" />
          </div>
        )}
      </div>
    </MobileModal>
  );
};
